import React, { useEffect, useState } from "react";
import AdminLayout from "../../layout/AdminLayout";
import AddScreen from "./AddScreen";
import { IoMdTrash } from "react-icons/io";
import { FiEdit } from "react-icons/fi";
import { deleteScreen, getAllScreens } from "../../../api/screens.api";
import { confirmScreenDelete, success } from "../../../utils/notifications";
import { RotatingLines } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";

const initialData = {
  status: "",
  model: "",
  vehicle: "",
};

function Screens() {
  const [addScreenOpen, setAddScreenOpen] = useState(false);
  const [screensData, setScreensData] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchScreensData = () => {
    setLoading(true);
    getAllScreens()
      .then((res) => {
        setLoading(false);
        setScreensData(res?.data?.screens);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching screens data:", error);
      });
  };

  useEffect(() => {
    fetchScreensData();
  }, []);

  const handleDelete = (id) => {
    confirmScreenDelete().then(async (result) => {
      if (result.isConfirmed) {
        await deleteScreen(id);
        setScreensData(screensData.filter((screen) => screen._id !== id));
        await success("Success!", "Screen deleted successfully!");
      }
    });
  };

  // Function to truncate long text with ellipsis
  const truncateText = (text, maxLength = 20) => {
    if (!text) return "-";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  return (
    <>
      <AddScreen
        open={addScreenOpen}
        setOpen={setAddScreenOpen}
        fetchScreensData={fetchScreensData}
        initialData={initialData}
      />
      <AdminLayout title="Screens">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <RotatingLines
              strokeColor="black"
              strokeWidth="5"
              animationDuration="0.75"
              width="96"
              visible={true}
            />
          </div>
        ) : (
          <div className="mx-2 sm:mx-4 lg:mx-8">
            {/* Empty state */}
            {screensData.length === 0 && (
              <div className="text-center py-10">
                <p className="text-gray-500 text-lg">No screens found</p>
              </div>
            )}

            {/* Screens Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {screensData.map((screen, index) => (
                <div
                  key={index}
                  className="cursor-pointer transition-all duration-300 hover:shadow-lg"
                  onClick={() => navigate(`/screendetails/${screen._id}`)}
                >
                  <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col border border-gray-100">
                    {/* Header with status */}
                    <div className="px-4 py-3 flex justify-between items-center bg-yellow-500 ">
                      <h3 className="font-semibold text-gray-800">
                        {truncateText(screen?.model, 25)}
                      </h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(screen._id);
                          }}
                          className="text-gray-600 hover:text-red-600"
                        >
                          <IoMdTrash size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Main content */}
                    <div className="p-4 flex-1">
                      {/* Status indicators */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Screen Status</p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              screen?.status === "Active"
                                ? "bg-green-100 text-green-800"
                                : screen?.status === "In Active"
                                ? "bg-orange-100 text-orange-800"
                                : screen?.status === "Under Maintenance"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {screen?.status || "Unknown"}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">
                            Display Status
                          </p>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              screen?.onlineStatus === "online"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {screen?.onlineStatus || "offline"}
                          </span>
                        </div>
                      </div>

                      {/* Type and Model */}
                      <div className="space-y-2 mb-4">
                        <div>
                          <p className="text-xs text-gray-500">Screen Type</p>
                          <p className="text-sm font-medium text-gray-900">
                            {screen?.screenType || "-"}
                          </p>
                        </div>
                      </div>

                      {/* Dynamic content based on screen type */}
                      <div className="border-t border-gray-100 pt-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          {screen?.screenType === "vehicle"
                            ? "Vehicle Details"
                            : screen?.screenType === "restaurant"
                            ? "Restaurant Details"
                            : "Details"}
                        </h4>

                        {screen?.screenType === "vehicle" && (
                          <div className="space-y-2">
                            {[
                              { label: "Brand", value: screen?.vehicle?.brand },
                              { label: "Model", value: screen?.vehicle?.model },
                              { label: "Type", value: screen?.vehicle?.type },
                              {
                                label: "Location",
                                value: screen?.vehicle?.location,
                              },
                            ].map((item, i) => (
                              <div key={i}>
                                <p className="text-xs text-gray-500">
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {truncateText(item.value, 30) || "-"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {screen?.screenType === "restaurant" && (
                          <div className="space-y-2">
                            {[
                              {
                                label: "Name",
                                value: screen?.restaurant?.name,
                              },
                              {
                                label: "Location",
                                value: screen?.restaurant?.location,
                              },
                              {
                                label: "Cuisine",
                                value: screen?.restaurant?.cuisineType,
                              },
                              {
                                label: "Capacity",
                                value: screen?.restaurant?.seatingCapacity,
                              },
                              {
                                label: "Phone",
                                value:
                                  screen?.restaurant?.contactInformation?.phone,
                              },
                            ].map((item, i) => (
                              <div key={i}>
                                <p className="text-xs text-gray-500">
                                  {item.label}
                                </p>
                                <p className="text-sm font-medium text-gray-900 break-words">
                                  {item.label === "Phone"
                                    ? item.value || "-"
                                    : truncateText(item.value, 30) || "-"}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
}

export default Screens;
